/**
 * REST File Storage Adapter
 * Calls the ModernizeIT Files API for file operations
 */

const DEFAULT_BASE_URL = 'http://localhost:8000'

export class RestFileStorageAdapter {
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || DEFAULT_BASE_URL
  }

  /**
   * Build the API URL for file operations
   */
  _buildUrl(account, app, endpoint) {
    return `${this.baseUrl}/files/${account}/${app}${endpoint}`
  }

  /**
   * List COBOL source files
   */
  async listCobolFiles(account, app) {
    const url = this._buildUrl(account, app, '/cobol')
    const response = await fetch(url)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to list COBOL files')
    }

    const data = await response.json()
    return data.files || []
  }

  /**
   * List Java workspace files
   */
  async listJavaFiles(account, app) {
    const url = this._buildUrl(account, app, '/java')
    const response = await fetch(url)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to list Java files')
    }

    const data = await response.json()
    return data.files || []
  }

  /**
   * Read COBOL file content (read-only)
   */
  async readCobolFile(account, app, filePath) {
    const url = this._buildUrl(account, app, `/cobol/${filePath}`)
    const response = await fetch(url)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to read COBOL file')
    }

    const data = await response.json()
    return data.content
  }

  /**
   * Read Java file content
   */
  async readJavaFile(account, app, filePath) {
    const url = this._buildUrl(account, app, `/java/${filePath}`)
    const response = await fetch(url)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to read Java file')
    }

    const data = await response.json()
    return data.content
  }

  /**
   * Save Java file content
   */
  async saveJavaFile(account, app, filePath, content) {
    const url = this._buildUrl(account, app, `/java/${filePath}`)
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to save Java file')
    }

    return await response.json()
  }

  /**
   * Get workspace status
   */
  async getWorkspaceStatus(account, app) {
    const url = this._buildUrl(account, app, '/workspace/status')
    const response = await fetch(url)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to get workspace status')
    }

    return await response.json()
  }

  /**
   * Reset workspace from generated files
   */
  async resetWorkspace(account, app) {
    const url = this._buildUrl(account, app, '/workspace/reset')
    const response = await fetch(url, { method: 'POST' })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to reset workspace')
    }

    return await response.json()
  }

  /**
   * Get section mappings (COBOL paragraphs to Java methods)
   */
  async getSections(account, app) {
    const url = this._buildUrl(account, app, '/sections')
    const response = await fetch(url)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to get sections')
    }

    const data = await response.json()
    return data.sections || []
  }

  // =========================================================================
  // Adapter interface methods (matching MockFileStorageAdapter)
  // These methods are kept for compatibility but require account/app context
  // =========================================================================

  /**
   * Get project files structure
   * @param {Object} context - { account, app } context
   */
  async getProjectFiles(context) {
    if (!context?.account || !context?.app) {
      console.warn('[RestFileStorageAdapter] Missing account/app context')
      return { cobol: [], java: [] }
    }

    try {
      const [cobolFiles, javaFiles] = await Promise.all([
        this.listCobolFiles(context.account, context.app),
        this.listJavaFiles(context.account, context.app)
      ])

      // Transform to the expected format
      return {
        cobol: cobolFiles.map(f => ({
          id: f.name.toLowerCase().replace(/\.[^.]+$/, ''),
          name: f.name,
          path: f.path,
          type: 'cobol'
        })),
        java: javaFiles.map(f => ({
          id: f.name.toLowerCase().replace(/\.[^.]+$/, ''),
          name: f.name,
          path: f.path,
          type: 'java'
        }))
      }
    } catch (err) {
      console.error('[RestFileStorageAdapter] getProjectFiles error:', err)
      return { cobol: [], java: [] }
    }
  }

  /**
   * Get COBOL file with sections
   * @param {Object} context - { account, app } context
   * @param {string} fileId - File ID (e.g., 'lgacus01')
   */
  async getCobolFileWithSections(context, fileId) {
    if (!context?.account || !context?.app) {
      throw new Error('Missing account/app context')
    }

    // Find the file by ID
    const cobolFiles = await this.listCobolFiles(context.account, context.app)
    const file = cobolFiles.find(f =>
      f.name.toLowerCase().replace(/\.[^.]+$/, '') === fileId.toLowerCase()
    )

    if (!file) {
      throw new Error(`COBOL file not found: ${fileId}`)
    }

    // Read the file content
    const content = await this.readCobolFile(context.account, context.app, file.path)

    // Get sections from the API
    const sections = await this.getSections(context.account, context.app)

    // Transform sections to match expected format
    const transformedSections = sections.map((s, index) => ({
      id: `section-${index}`,
      name: s.cobol_name,
      displayName: s.cobol_name,
      startLine: s.cobol_start_line,
      endLine: s.cobol_end_line,
      type: 'paragraph',
      status: s.status === 'converted' ? 'converted' : 'pending',
      javaMethod: s.java_method,
      javaClass: s.java_class
    }))

    return {
      name: file.name,
      content,
      sections: transformedSections
    }
  }

  /**
   * Read file content
   * @param {Object} context - { account, app } context
   * @param {string} path - File path (e.g., '/java/CustomerService.java' or '/cobol/LGACUS01.CBL')
   */
  async readFile(context, path) {
    if (!context?.account || !context?.app) {
      throw new Error('Missing account/app context')
    }

    if (path.startsWith('/java/')) {
      return await this.readJavaFile(context.account, context.app, path.slice(6))
    } else if (path.startsWith('/cobol/')) {
      return await this.readCobolFile(context.account, context.app, path.slice(7))
    }

    throw new Error(`Unknown file type: ${path}`)
  }

  /**
   * Write file content (Java only)
   * @param {Object} context - { account, app } context
   * @param {string} path - File path (e.g., '/java/CustomerService.java')
   * @param {string} content - File content
   */
  async writeFile(context, path, content) {
    if (!context?.account || !context?.app) {
      throw new Error('Missing account/app context')
    }

    if (!path.startsWith('/java/')) {
      throw new Error('Can only write to Java files')
    }

    return await this.saveJavaFile(context.account, context.app, path.slice(6), content)
  }

  /**
   * List files in a directory
   * @param {Object} context - { account, app } context
   * @param {string} path - Directory path (e.g., '/java' or '/cobol')
   */
  async listFiles(context, path = '/') {
    if (!context?.account || !context?.app) {
      return []
    }

    if (path === '/java' || path.startsWith('/java')) {
      return await this.listJavaFiles(context.account, context.app)
    } else if (path === '/cobol' || path.startsWith('/cobol')) {
      return await this.listCobolFiles(context.account, context.app)
    }

    return []
  }

  /**
   * Get Java skeleton (not implemented in REST API - returns empty)
   */
  async getJavaSkeleton(context) {
    // This could be implemented to get the initial generated Java class
    // For now, return a basic skeleton
    return `/**
 * Generated Java Class
 *
 * This class was generated from COBOL source code.
 * Add your implementation here.
 */
public class GeneratedClass {

    // TODO: Add fields and methods

}
`
  }

  // Placeholder methods for interface compatibility
  async deleteFile() { throw new Error('Not implemented') }
  async uploadFiles() { throw new Error('Not implemented') }
  async uploadAndExtractZip() { throw new Error('Not implemented') }
  async createDirectory() { throw new Error('Not implemented') }
  async deleteDirectory() { throw new Error('Not implemented') }
  async getDownloadUrl() { throw new Error('Not implemented') }
  async downloadAsZip() { throw new Error('Not implemented') }
  async getStoragePath() { throw new Error('Not implemented') }
  async getStorageUsage() { throw new Error('Not implemented') }
  async fileExists() { throw new Error('Not implemented') }
}

/**
 * Factory function to create the adapter
 */
export function createRestFileStorageAdapter(options = {}) {
  return new RestFileStorageAdapter(options)
}
